import { useEffect } from "react";
import { useActiveTrackingPixels } from "@/hooks/useTrackingPixels";

// Declare fbq and gtag for TypeScript
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

interface TrackingPixelsProps {
  trackPurchase?: boolean;
  purchaseValue?: number;
  purchaseCurrency?: string;
  orderId?: string;
}

export function TrackingPixels({
  trackPurchase = false,
  purchaseValue,
  purchaseCurrency = "AED",
  orderId,
}: TrackingPixelsProps) {
  const { pixels } = useActiveTrackingPixels();

  // Initialize Google Analytics (GA4)
  useEffect(() => {
    const gaPixels = pixels.filter((p) => p.pixel_type === "google_analytics" && p.track_page_view);

    if (gaPixels.length === 0) return;

    gaPixels.forEach((pixel) => {
      // Skip if this specific gtag script is already loaded
      const scriptId = `gtag-script-${pixel.pixel_id}`;
      if (document.getElementById(scriptId)) return;

      // Load the gtag.js library
      const gtagScript = document.createElement("script");
      gtagScript.id = scriptId;
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${pixel.pixel_id}`;
      document.head.appendChild(gtagScript);

      // Initialize dataLayer and gtag
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
        window.gtag("js", new Date());
      }

      window.gtag("config", pixel.pixel_id, {
        send_page_view: true,
      });
    });
  }, [pixels]);

  // Track GA4 page views on route change
  useEffect(() => {
    const gaPixels = pixels.filter((p) => p.pixel_type === "google_analytics" && p.track_page_view);

    if (gaPixels.length === 0 || !window.gtag) return;

    gaPixels.forEach((pixel) => {
      window.gtag("config", pixel.pixel_id, {
        page_path: window.location.pathname + window.location.search,
      });
    });
  }, [pixels, window.location.pathname]);

  // Initialize Meta Pixel
  useEffect(() => {
    const metaPixels = pixels.filter((p) => p.pixel_type === "meta" && p.track_page_view);

    if (metaPixels.length === 0) return;

    // Load Meta Pixel base code if not already loaded
    if (!window.fbq) {
      const script = document.createElement("script");
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
      `;
      document.head.appendChild(script);
    }

    // Initialize each pixel
    metaPixels.forEach((pixel) => {
      if (window.fbq) {
        window.fbq("init", pixel.pixel_id);
        window.fbq("track", "PageView");
      }
    });
  }, [pixels]);

  // Track purchase event
  useEffect(() => {
    if (!trackPurchase || !purchaseValue) return;

    // Meta purchase tracking
    const metaPixels = pixels.filter((p) => p.pixel_type === "meta" && p.track_purchase);
    metaPixels.forEach((pixel) => {
      if (window.fbq) {
        window.fbq("init", pixel.pixel_id);
        window.fbq("track", "Purchase", {
          value: purchaseValue,
          currency: purchaseCurrency,
          content_type: "product",
          order_id: orderId,
        });
      }
    });

    // GA4 purchase tracking
    const gaPixels = pixels.filter((p) => p.pixel_type === "google_analytics" && p.track_purchase);
    gaPixels.forEach(() => {
      if (window.gtag) {
        window.gtag("event", "purchase", {
          transaction_id: orderId,
          value: purchaseValue,
          currency: purchaseCurrency,
        });
      }
    });
  }, [trackPurchase, purchaseValue, purchaseCurrency, orderId, pixels]);

  // Render noscript fallbacks for SEO
  const metaPixels = pixels.filter((p) => p.pixel_type === "meta");

  return (
    <>
      {metaPixels.map((pixel) => (
        <noscript key={pixel.id}>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src={`https://www.facebook.com/tr?id=${pixel.pixel_id}&ev=PageView&noscript=1`}
            alt=""
          />
        </noscript>
      ))}
    </>
  );
}

// Helper function to track custom events
export function trackMetaEvent(eventName: string, params?: Record<string, unknown>) {
  if (window.fbq) {
    window.fbq("track", eventName, params);
  }
}

// Helper function to track GA4 custom events
export function trackGAEvent(eventName: string, params?: Record<string, unknown>) {
  if (window.gtag) {
    window.gtag("event", eventName, params);
  }
}
